/* Dynamic Plot code copyright 2024 (c) Tuva Labs Inc.
 *
 * created by cmiller
 */

import { BigCard } from "./ElemBigView";

// export const bigCardWidth
// export const bigCardHeight

/**
 * Each ElemCard object handles a small card, one per post-filter case, in the Tuva Jr Play View
 *      - the card outline is the visible outline of the card in the 'Cards' area.
 *      - the card back is the visible, draggable card that moves from 'Cards' to the Play area.
 *      - the card 'face' is handled by class BigCard, once the small card is dragged to a big card
 */
export class ElemCard {

    constructor( caseIndex, smallCardIndex, rect, playContainer, iconStyler  ) {
        let cardOutline = ElemCard.newCardOutline( playContainer, rect ),
            cardPlayOutline = ElemCard.newCardOutline( playContainer, rect ),
            cardBack = ElemCard.newCardBack( playContainer, iconStyler, caseIndex, smallCardIndex, rect /*corner*/ );

        // identity
        this.caseIndex = caseIndex; // case index, pre-filter
        this.smallCardIndex = smallCardIndex; // varies from caseIndex when filtering
        // visible elements
        this.outline = cardOutline; // dashed outline SVG element
        this.plotOutline = cardPlayOutline; // extra outline for when in plot AND open in big card; null when not in plot
        this.back = cardBack; // card number and icon SVG element
        // state (indicating which part of play area the card is in)
        this.setIsHome();
        this.plotCoords = null; // location in plot if plotting, otherwise null
        this.dragCoords = null; // location card was dragged to (from home, or from plot)

        this.showHideCard( playContainer.options.isIconOnly, playContainer.Dyn );

        // add cross-references
        cardOutline.data("card", this );
        cardBack.data("card", this );
    }

    destroy() {
        // remove raphael elements from paper before destroying
        this.outline.remove();
        delete this.outline;
        this.plotOutline.remove();
        delete this.plotOutline;
        this.back.remove();
        delete this.back;
    }

    get cardNo() { return this.smallCardIndex+1 }

    static cardBorder = 15;     // size of border in card home
    static cardCorner = 8;
    static scale = 0.25; // small card is 1/4 of big card size (same proportions)
    static textYOffset = 0.2;    // fraction of height from top
    static iconYOffset = 0.6;    // fraction of height from top
    static tipYOffset = 0.9;     // fraction of height from top
    static tipXOffset = 1.0;      // fraction of width from left
    static cardWidth = Math.round( BigCard.width * ElemCard.scale)
    static cardHeight = Math.round( BigCard.height * ElemCard.scale)
    static cardIconSize = 0.5 * ElemCard.cardWidth;
    static cardIconBottom = (ElemCard.cardHeight * ElemCard.iconYOffset) + ElemCard.cardIconSize/2;
    static cardOutlineStroke = "#808080"; // "gray";
    static cardOutlineDash = '3,3';
    static cardOutlineFill = "#FFF";
    static cardOutlineDropFill = "#eee";
    static cardBackStroke = "#1D4CBC";
    static cardBackHighlight = "#001F87";
    static cardBackFill = "#E6E9F7";
    static cardNumFontSize = 14;
    static cardStrokeWidth = 2; // stroke width of the card back rectangle

    /**
     * Create the small card outline visible element, for the Cards home view.
     * @param playContainer
     * @param rect
     * @returns {*} -- SVG/Raphael rectangle element, rounded with dashed outline
     */
    static newCardOutline( playContainer, rect /*, corner*/ ) {
        let outline = playContainer.paper.rect( 0, 0, rect.width, rect.height, this.cardCorner )
            .attr({ stroke:ElemCard.cardOutlineStroke, fill:ElemCard.cardOutlineFill});
        outline.node.setAttribute('stroke-dasharray', ElemCard.cardOutlineDash );
        return outline;
    }

    /**
     * Create a new small card back, which has a round-rect, icon, and card no., and is draggable.
     * And it has an icon-only state, where the round-rect and card no are hidden.
     * Caller must set the icon-only state. Here we create it in the isIconOnly===false state.
     * @param playContainer -- container with has SVG paper to add to
     * @param iconStyler - styler which holds custom icon, if any
     * @param caseIndex - case index of this card, in dataset
     * @param smallCardIndex - card index, different from case index if cases are filtered out.
     * @param bbox - position of card back
     * @returns {*}
     */
    static newCardBack( playContainer, iconStyler, caseIndex, smallCardIndex, bbox /*, corner*/ ) {
        let Dyn = playContainer.Dyn,
            paper = playContainer.paper;

        paper.setStart();
        let rrect = createRectEl( paper, bbox ),
            textEl = createTextEl( paper, ""+(1+smallCardIndex) ),
            iconEl = createIconEl( paper, bbox );
        let set = paper.setFinish();
        set.getRectEl = () => { return rrect; }
        set.getIconEl = () => { return iconEl; }
        set.getTextEl = () => { return textEl; }
        set.translate( bbox.x, bbox.y ); // use transform to move in paper coordinates
        // Note: caller should call ElemCard.showHideCard() to show/hide/enable/disable elements
        return set;

        function createRectEl( paper, bbox ) {
            let rrect = paper.rect( 0, 0, bbox.width, bbox.height, ElemCard.cardCorner )
                    .attr({ stroke:ElemCard.cardBackStroke, 'stroke-width':ElemCard.cardStrokeWidth, fill:ElemCard.cardBackFill});
            setClickDragFocusHover( rrect, true );
            return rrect;
        }

        function createTextEl( paper, textStr ) {
            // text and icon are not draggable, except icon is draggable when isIconOnly===true
            const textY = bbox.height*ElemCard.textYOffset;
            let textEl = paper.text( bbox.width*0.5, textY, ""+(1+smallCardIndex))
                    .attr({"font-size":ElemCard.cardNumFontSize});
            Dyn.plotUtil.disableMouseOverEvents( textEl );
            return textEl;
        }

        function createIconEl( paper, bbox ) {
            const iconY = bbox.height*ElemCard.iconYOffset,
                iconRadius = ElemCard.cardIconSize/2,
                pathStr = iconStyler.hasCustomCaseIcon() ?
                    iconStyler.calcCustomIconPath( bbox.width*0.5, iconY, iconRadius ) :
                    iconStyler.calcCirclePath( bbox.width*0.5, iconY, iconRadius );
            let iconEl = paper.path( pathStr )
                    .attr({ fill:Dyn.plotUtil.kCaseColor });
            setClickDragFocusHover( iconEl, false );
            return iconEl;
        }

        /** set up the handlers for click, drag, keyboard focus, etc. that
         * are common to the rectangle or icon, depending on isIconOnly true or folse.
         * @param elem
         */
        function setClickDragFocusHover( elem, isActive ) {
            elem.data("caseIndex",caseIndex)
                .data("smallCardIndex",smallCardIndex)
                .data("tabIndex", 0 ) // NOTE: used to set/restore tabindex after enable/disable
                .hover( hoverIn, hoverOut );
            // Drag and drop handlers
            elem.drag( dragCardMove, dragCardStart, dragCardEnd );
            elem.node.onfocus = focusHandler; // on focus, set aria-label, show tooltip, etc
            elem.node.onblur = blurHandler;
            Dyn.plotUtil.setKeyboardFocusable( elem, keyDownHandler.bind( elem ) );
            elem.getSet = () => { return set; }
            // save original x,y offset from 0,0, and width/height, to create plot outline
            const bbox = elem.getBBox();
            elem.data("plotOutline", { x:bbox.x, y:bbox.y, width:bbox.width, height:bbox.height });
            // NOTE: see showHideCard() for enabling/disabling this element
        }

        function showToolTip( set ) {
            let rrect = set.getRectEl(),
                caseIndex = rrect.data("caseIndex"),
                smallCardIndex = rrect.data("smallCardIndex"),
                bbox = rrect.getBBox();
            rrect.data("tooltip", playContainer.getCardToolTip( caseIndex, smallCardIndex ));
            Dyn.toolTip.show( rrect, event, {
                x: bbox.x + bbox.width*ElemCard.tipXOffset,
                y: bbox.y + bbox.height*ElemCard.tipYOffset
            } );
        }

        function hideToolTip( Dyn, set ) {
            let rrect = set.getRectEl();
            rrect.data( { "tooltip": "" });
            if( Dyn.toolTip.isMyElement( rrect ))
                Dyn.toolTip.hide();
        }

        function keyDownHandler( event ) {
            // this === rrect OR iconEl
            switch( event.key ) { // const {key} = event; ES6+
                case "Enter":
                    showToolTip( set );
                    // Open current case in editable case card view
                    playContainer.editableView.cardView.goToCase(caseIndex);
                    break;
                case "Escape":
                    hideToolTip( Dyn, set );
                    break;
                case "ArrowLeft":
                case "ArrowRight":
                case "ArrowUp":
                case "ArrowDown": {
                    const d = 20; // move this element 20px per keypress, in arrow's direction
                    let dist = {
                        dx: (event.key==="ArrowLeft")? -d : ((event.key==="ArrowRight")? d: 0 ),
                        dy: (event.key==="ArrowUp")  ? -d : ((event.key==="ArrowDown") ? d: 0 )
                    };
                    dragCardStart.call( this, undefined, undefined, event );
                    dragCardMove.call( this, dist.dx, dist.dy, undefined, undefined, event, true );
                    dragCardEnd.call( this, event );
                }   break;
            }
        }

        // keyboard focus
        function focusHandler( event ) {
            let rrect = set.getRectEl(),
                iconEl = set.getIconEl();
            // update and show tool tip on keyboard focus
            showToolTip( set );

            // aria-label of the visible element
            if( Dyn.plotUtil.isElementVisible(rrect))
                rrect.node.setAttribute( "aria-label", rrect.data("tooltip") );
            if( Dyn.plotUtil.isElementVisible(iconEl))
                iconEl.node.setAttribute( "aria-label", rrect.data("tooltip") );
        }

        // when keyboard focus leaves this icon
        function blurHandler( event ) {
            //let set = this.getSet(); // this === rrect OR icon
            hideToolTip( Dyn, set );
        }

        function hoverIn( event ) { // called with hoverIn event, or dragStart/dragMove on touch devices
            let set = this.getSet(), // this === rrect OR icon
                rrect = set.getRectEl(),
                iconEl = set.getIconEl(),
                wasFocus = Dyn.plotUtil.isKeyboardFocus( this );
            set.toFront();
            if( playContainer.options.isIconOnly ) {
                playContainer.home.addGlow(iconEl);
            } else {
                rrect.attr( "stroke", ElemCard.cardBackHighlight );
            }
            showToolTip( set );
            if( wasFocus ) {
                Dyn.plotUtil.setAsKeyboardFocus( this );
            }
        }
        function hoverOut( event ) { // called with hoverOut event, or dragEnd on touch devices
            let set = this.getSet(), // this === rrect OR icon
                rrect = set.getRectEl(),
                wasFocus = Dyn.plotUtil.isKeyboardFocus( this );
            playContainer.home.removeGlow(); // remove glow if any
            rrect.attr( "stroke", ElemCard.cardBackStroke ); // restore stroke
            hideToolTip( Dyn, set );
            playContainer.layerElements();
            if( wasFocus ) {
                Dyn.plotUtil.setAsKeyboardFocus( this );
            }
        }
        function dragCardStart( x, y, event ) {
            if(playContainer.isEditing) {
                return; // do not drag if editing
            }
            let cardBack=this.getSet(), // this === rrect OR icon
                rrect=cardBack.getRectEl(),
                bbox = rrect.getBBox();
            rrect.data( "startX", bbox.x );
            rrect.data( "startY", bbox.y );
            rrect.data("dragResult", {
                result:"click", caseIndex:rrect.data("caseIndex"), smallCardIndex:rrect.data("smallCardIndex"),
                leftHome:false, /*onBigCard:false,*/ bigCardIndex:-1, transform:"", focus:this });
            cardBack.toFront();
            if( Dyn.environment.isTouchDevice() )
                hoverIn.call( this, event )
            Dyn.plotUtil.safeStopPropogation( event );
            //console.log("START", rrect.data("smallCardIndex") )
        }
        function dragCardMove( dx, dy, x, y, event, isKeyboardMove=false ) {
            if(playContainer.isEditing) {
                return; // do not drag if editing
            }
            if( Dyn.environment.isTouchDevice() ) {
                hoverIn.call( this, event );
            }
            let cardBack=this.getSet(), // this === rrect OR icon
                rrect=cardBack.getRectEl(),
                caseIndex = rrect.data("caseIndex"),
                smallCardIndex = rrect.data("smallCardIndex");
            cardBack.transform( "t"+(rrect.data("startX")+dx)+","+(rrect.data("startY")+dy));
            if( playContainer.options.isIconOnly && playContainer.home.glowElement ) { // if we have a glowElement created by hoverIn()
                playContainer.home.addGlow( cardBack.getIconEl() ); // move glow element
            }
            let isNotInPlot = ! playContainer.hasPlot(),
                cardBBox = new Dyn.mathUtil.Rect( rrect.getBBox()),
                cardHome = playContainer.home.isCardOverlappingHome( cardBBox, smallCardIndex ),
                cardBig = playContainer.big.isCardOverlappingBigCardArea( cardBBox, caseIndex ),
                cardConstraint = isCardOutsideDragArea( playContainer, cardBBox, smallCardIndex ),
                dragResult = rrect.data("dragResult");
            if( Dyn.toolTip.isMyElement( rrect )) {
                Dyn.toolTip.update( rrect, event, {
                    x: cardBBox.x + cardBBox.width*ElemCard.tipXOffset,
                    y: cardBBox.y + cardBBox.height*ElemCard.tipYOffset
                } );
            }
            dragResult.transform = null;
            if( cardConstraint && ! isKeyboardMove ) {
                dragResult.result = cardConstraint.animateHome ? "home" : "constrain";
                dragResult.transform = "t"+cardConstraint.x+","+cardConstraint.y;
                dragResult.xy = { x:cardConstraint.x, y:cardConstraint.y };
            } else if( cardHome && isNotInPlot && ! isKeyboardMove ) {
                dragResult.result = "home";
                dragResult.transform = "t"+cardHome.x+","+cardHome.y;
                if( dragResult.leftHome ) {
                    playContainer.home.highlightCardHome( true, smallCardIndex );
                }
            } else if(dx!==0 || dy!==0) {
                dragResult.result = "drag";
                dragResult.leftHome = true; // left home at least once
                dragResult.xy = { x:rrect.data("startX")+dx, y:rrect.data("startY")+dy };
                playContainer.home.highlightCardHome( false, smallCardIndex );
            }
            if( cardBig /*&& isNotInPlot*/ ) {
                if( dragResult.bigCardIndex >= 0 ) { // unhighlight old
                    playContainer.big.highlightBigCard( false, dragResult.bigCardIndex );
                }
                playContainer.big.highlightBigCard( true, cardBig.bigCardIndex, caseIndex );
                dragResult.result = "big card";
                dragResult.bigCardIndex = cardBig.bigCardIndex;
                dragResult.transform = ElemCard.transformToCoverBigCard( cardBBox, cardBig.bbox );
                // dragResult.onBigCard = true;
            } else {
                // dragResult.onBigCard = false;
                if( dragResult.bigCardIndex >= 0 ) {
                    playContainer.big.highlightBigCard( false, dragResult.bigCardIndex );
                }
            }
            //console.log("MOVE", dx, dy, dragResult )
            Dyn.plotUtil.safeStopPropogation( event );
        }
        function dragCardEnd( event ) {
            if(playContainer.isEditing) {
                if (event instanceof MouseEvent) {
                    // Open current case in editable case card view
                    playContainer.editableView.cardView.goToCase(caseIndex);
                }
                return; // do not drag if editing
            }
            let cardBack=this.getSet(), // this === rrect OR icon
                rrect=cardBack.getRectEl(),
                smallCard = rrect.data('card'),
                dragResult = rrect.data("dragResult");
            playContainer.home.highlightCardHome( false, smallCard.smallCardIndex );
            if( dragResult.bigCardIndex >= 0 ) {
                playContainer.big.highlightBigCard( false, dragResult.bigCardIndex );
                dragResult.focus = null;
            }
            Dyn.assert(["home","constrain","drag","big card","click"].includes(dragResult.result));
            if( dragResult.result==="home" ) {
                smallCard.setIsHome();
            } else if( ["constrain","drag"].includes(dragResult.result)) {
                smallCard.setIsDragged( dragResult.xy.x, dragResult.xy.y );
            } else if( dragResult.result==="click" && playContainer.hasPlot() ) {
                playContainer.big.clickOpenSmallCardInBigCard( smallCardIndex, caseIndex, 0 );
                // clear focus to avoid bug in Safari Version 17.6 (19618.3.11.11.5), where focus leaves animation trails
                Dyn.plotUtil.setAsKeyboardFocus( this, false );
                dragResult.focus = null;
            }
            if( dragResult?.transform ) {
                playContainer.dropAnimationStart( dragResult );
                smallCard.animateCardTo( dragResult.transform,
                    (dragResult.result==="big card") ? playContainer.slowAnimation(): playContainer.fastAnimation(),
                    null, () => dropAnimationEnd(dragResult) );
            } else {
                playContainer.updateHomeState();
            }
            if( Dyn.environment.isTouchDevice() )
                hoverOut.call( this, event );
            //console.log("-END", dragResult, smallCard )
            Dyn.plotUtil.safeStopPropogation( event );
            if( dragResult.focus ) {
                Dyn.plotUtil.setAsKeyboardFocus( this );
            }
        }
        function isCardOutsideDragArea( playContainer, cardBBox, smallCardIndex ) {
            let bin = playContainer.play.bins.find((bin) => bin.hasSmallCardIndex( smallCardIndex ));
            return bin ? bin.getBinConstraintForCard( cardBBox, playContainer.options.isIconOnly )
                       : playContainer.home.getDragConstraintForCard( cardBBox, smallCardIndex );
        }
        function dropAnimationEnd( dragResult ) {
            playContainer.dropAnimationEnd( dragResult );
            if( dragResult.focus && Dyn.plotUtil.isElementVisible(dragResult.focus)) {
                Dyn.plotUtil.setAsKeyboardFocus(dragResult.focus);
            }
        }
    }

    static transformToCoverBigCard( smallCardBBox, bigCardBBox ) {
        let scale = 0.93 * bigCardBBox.width / ElemCard.cardWidth;
        return "t"+(bigCardBBox.centerX()-smallCardBBox.width/2)+","+(bigCardBBox.centerY()-smallCardBBox.height/2)
            +"s"+scale+","+scale;
    }

    animateCardTo( transform, speed, iconFillColor=null, handleAnimationEnd=null ) {
        this.back.animate({ transform:transform }, speed.time, speed.easing, handleAnimationEnd );
        if( iconFillColor ) { // change color; otherwise leave color as-is
            this.back.getIconEl().animate( { fill: iconFillColor }, speed.time, speed.easing );
        }
    }

    moveCardTo( transform ) {
        this.back.attr({ transform:transform });
    }

    getHomeBBox() {
        return this.outline.getBBox();
    }

    save() {
        // save the card location in universal [0-1] coordinates based on position within paper
        let bbox = this.back.getBBox();
        return this._viewToNormalizedCoord( bbox.x, bbox.y )
    }

    restore( data, isDragged, speed, playContainer ) {
        // if the card was dragged, restore the custom card location from universal coordinates
        // NOTE: not used for cards that are home, in big card,
        // or in an ordered by, grouped by plot but never dragged.
        if( isDragged && data.x >= 0 && data.x <= 1 && data.y >=0 && data.y <= 1 ) {
            let vCoord = this._normalizedToViewCoord( data.x, data.y ), // (x,y) restored and scaled to view
                bin = playContainer.play.bins.find((bin) => bin.hasSmallCardIndex( this.smallCardIndex ));
            if( bin ) {
                // card was dragged within in a bin in a plot; make sure it is still in bin
                let oldBBox = this.back.getBBox() ,
                    restoredBBox = new playContainer.Dyn.mathUtil.Rect( vCoord.x, vCoord.y, oldBBox.width, oldBBox.height ),
                    constrainedXY = bin.getBinConstraintForCard( restoredBBox /*, this.caseIndex */ );
                if( constrainedXY ) { // if we need to adjust location
                    vCoord.x = constrainedXY.x;
                    vCoord.y = constrainedXY.y;
                }
            }
            // this.setIsHome(false);
            this.setIsDragged( vCoord.x, vCoord.y );
            this.animateCardTo( "t"+vCoord.x+","+vCoord.y, speed );
            //console.log("restore card", this.cardNo, this );
        }
    }

    _normalizedToViewCoord( x, y ) {
        let paper = this.outline.paper;
        return { x:x * paper.width, y:y * paper.height }
    }

    _viewToNormalizedCoord( x, y ) {
        const screenPrecision = 5;
        let paper = this.outline.paper;
        return { x:(x / paper.width).toPrecision(screenPrecision), y:(y / paper.height).toPrecision(screenPrecision) }
    }

    /**
     * Update the visibility of the card, with 3 states:
     * (1) Hide the card entirely (if isMissing==true)
     * or (2) show the card Back (outline+number+icon)
     * or (3) show the icon only (if isIconOnly==true)
     * @param isIconOnly
     * @param Dyn
     */
    showHideCard( isIconOnly, Dyn ) {
        // console.log("showHideCard", this.smallCardIndex+1, this );
        let rectEl = this.back.getRectEl(),
            textEl = this.back.getTextEl(),
            iconEl = this.back.getIconEl();
            //isMissing = rectEl.data("isMissing");
        if( this.isMissing || this.isBig ) { // hide entire card if in plot and missing or in big card
            hide( rectEl );
            hide( textEl );
            hide( iconEl );
        } else if( isIconOnly ) {
            hide( rectEl );
            hide( textEl );
            show( iconEl, true, iconEl.data("tabIndex"));
            Dyn.plotUtil.enableMouseOverEvents( iconEl );
        } else { // not missing and not icon-only
            show( rectEl, true, rectEl.data("tabIndex"));
            show( textEl, false );
            show( iconEl, false );
        }
        if( this.plotCoords && (this.isBig || this.isDragged )) {
            show( this.plotOutline, false ); // show outline in plotting position, if plotted and dragged
        } else {
            hide( this.plotOutline );
        }
        function hide( elem ) {
            elem.hide();
            Dyn.plotUtil.disableMouseOverEvents( elem );
            Dyn.plotUtil.disableKeyboardFocus( elem );
            elem.node.setAttribute( "tabindex", -1 );
        }
        function show( elem, canMouseAndKeyboard, tabIndex ) {
            elem.show();
            if( canMouseAndKeyboard ) {
                Dyn.plotUtil.enableMouseOverEvents( elem );
                Dyn.plotUtil.enableKeyboardFocus( elem );
                elem.node.setAttribute( "tabindex", tabIndex );
            } else {
                Dyn.plotUtil.disableMouseOverEvents( elem );
                Dyn.plotUtil.disableKeyboardFocus( elem );
                elem.node.setAttribute( "tabindex", -1 );
            }
        }
    }

    hideCardUnderBigCard( bigCardBBox, Dyn ) {
        if( bigCardBBox ) {
            // if supplied, move the card to the bigCardBBox for later reveal with animateCardTo()
            let cardBBox = this.getHomeBBox(),
                transform = ElemCard.transformToCoverBigCard( cardBBox, bigCardBBox );
            this.moveCardTo( transform );
            this.setIsBig();
            this.showHideCard( false, Dyn );
        }
        this.back.hide();
    }

    _setState( state ) {
        this.isHome = state==='home';
        this.isDragged = state==='dragged';
        this.isBig = state==='big';
        this.isMissing = state==='missing';
        this.isPlotted = state==='plotted';
        if( ! this.isDragged ) {
            this.dragCoords = null;
        }
        if( ! (this.isPlotted || this.isBig || this.isDragged )) {
            this.plotCoords = null;
        }
        if( !( this.isHome || this.isBig || this.isMissing || this.isPlotted || this.isDragged ))
            console.log( "Card in unknown state", this );
        // console.log( "_setState()", this.caseIndex+1, state, this.plotCoords, this.dragCoords );
    }

    /** is the card back in its home location (over its outline?) if true, it is home.
     * If not home, it might be dragged, in its plot home, missing (hidden), or in a big card.
     * See also setIsPlotted()
     * @param isHome
     */
    setIsHome() {
        // update the state of this card, indicating where in the play area it is
        this._setState('home');
    }

    setIsDragged( x, y ) {
        this._setState('dragged');
        // saved normalized coordinates for save/restore to the dragged position
        this.dragCoords = this._viewToNormalizedCoord( x, y );
    }

    /** is the small card open in a big card?  If so we usually hide the small card */
    setIsBig() {
        this._setState( 'big' );
    }

    /** if plotting but not plottable, set the state to missing, so that the card can not be plotted (group by/order by),
     *  and so should be hidden from the plot. */
    setIsMissing() {
        this._setState( 'missing' );
        if( this.isMissing ) {
            console.log( "setIsMissing", this.isMissing, this );
        }
    }

    // isMissing() {
    //     return this.isMissing;
    // }

    /**
     * set card state to being plotted, at the given position.
     * set the temporary home location of the small card, when plotting with OrderBy or GroupBy.
     * Cleared by setIsHome() */
    setIsPlotted( x, y, isIconOnly, isReturnToPlot = false ) {
        this._setState( 'plotted' );
        if( ! isReturnToPlot ) {
            // save the plot view coords, for return to plot after drop on big cards
            this.plotCoords = { x:x, y:y };
            // move plot outline to plotted card/icon position to show when dragging/in big card (see showHideCard())
            const visibleElement = ( isIconOnly ? this.back.getIconEl() : this.back.getRectEl()),
                bbox = visibleElement.data("plotOutline");
            this.plotOutline.attr( { x:x+bbox.x, y:y+bbox.y, width:bbox.width, height:bbox.height } );
        }
    }

    getPlotCoords() {
        return this.plotCoords;
    }

    getCardState( playContainer /* ElemPlayContainer */) {
        if( playContainer.play.isCardInPlayArea( this.smallCardIndex )) {
            if( ! this.isPlotted ) console.log("Card in play area but not .isPlotted", this )
            return "in bin";
        } else if( playContainer.big.isCardInBigCardArea( this.caseIndex )) {
            if( ! this.isBig ) console.log("Card in big card area but not .isBig", this )
            return "in big card";
        } else if( this.isMissing ) {
            return "missing";
        } else if( this.isDragged ) {
            return "dragged";
        } else if( this.isHome ) {
            return "home";
        } else {
            return "unknown";
        }
    }

    /** get the fill color for the case icon, with optional attribute to color by attribute */
    static getCaseColor( Dyn, attribute, caseIndex ) {
        let colorScheme = {
                colorAttribute: attribute,
                caseFillColor: Dyn.plotUtil.kCaseColor,
            },
            color = Dyn.colorUtil.calcCaseColor( caseIndex, false, colorScheme );
        return color.colorString;
    }
}
